/**
 * Risk Scoring Utility for SOC Dashboard
 * Calculates risk scores based on multiple security factors and classifies severity levels
 */

/**
 * Severity classification thresholds
 */
export const SEVERITY_LEVELS = {
  CRITICAL: { min: 70, max: 100, label: 'CRITICAL', color: '#dc3545' },
  HIGH: { min: 40, max: 69, label: 'HIGH', color: '#fd7e14' },
  MEDIUM: { min: 20, max: 39, label: 'MEDIUM', color: '#ffc107' },
  LOW: { min: 0, max: 19, label: 'LOW', color: '#28a745' }
};

/**
 * Weight factors for risk calculation
 */
const RISK_WEIGHTS = {
  fileCount: 0.20,        // 20% weight - number of affected files
  dataVolume: 0.25,       // 25% weight - volume of data transferred (MB)
  offHoursActivity: 0.15, // 15% weight - activity during off-hours
  failedAttempts: 0.20,   // 20% weight - number of failed access attempts
  uniqueIPs: 0.20         // 20% weight - number of unique IP addresses
};

/**
 * Normalizes a value to a 0-100 scale based on thresholds
 * @param {number} value - The value to normalize
 * @param {number} lowThreshold - Low threshold value
 * @param {number} highThreshold - High threshold value (max scale)
 * @returns {number} Normalized value between 0 and 100
 */
const normalizeValue = (value, lowThreshold, highThreshold) => {
  if (value <= lowThreshold) return 0;
  if (value >= highThreshold) return 100;
  return ((value - lowThreshold) / (highThreshold - lowThreshold)) * 100;
};

/**
 * Calculates a comprehensive risk score based on multiple security factors
 * @param {Object} factors - Object containing risk factors
 * @param {number} factors.fileCount - Number of files involved in the incident
 * @param {number} factors.dataVolumeMB - Volume of data in megabytes
 * @param {number} factors.offHoursActivityCount - Number of activities during off-hours
 * @param {number} factors.failedAttempts - Number of failed access/authentication attempts
 * @param {number} factors.uniqueIPCount - Number of unique IP addresses involved
 * @returns {number} Risk score between 0 and 100
 */
export const calculateRiskScore = (factors = {}) => {
  // Extract factors with defaults
  const {
    fileCount = 0,
    dataVolumeMB = 0,
    offHoursActivityCount = 0,
    failedAttempts = 0,
    uniqueIPCount = 0
  } = factors;

  // Normalize each factor to 0-100 scale
  // File count: 0-5 files = low risk, 50+ files = high risk
  const fileScore = normalizeValue(fileCount, 5, 50);

  // Data volume: 0-100MB = low risk, 1000MB+ = high risk
  const dataVolumeScore = normalizeValue(dataVolumeMB, 100, 1000);

  // Off-hours activity: 0-2 events = low risk, 20+ events = high risk
  const offHoursScore = normalizeValue(offHoursActivityCount, 2, 20);

  // Failed attempts: 0-3 attempts = low risk, 30+ attempts = high risk
  const failedAttemptsScore = normalizeValue(failedAttempts, 3, 30);

  // Unique IPs: 0-2 IPs = low risk, 20+ IPs = high risk
  const uniqueIPScore = normalizeValue(uniqueIPCount, 2, 20);

  // Calculate weighted risk score
  const riskScore = (
    fileScore * RISK_WEIGHTS.fileCount +
    dataVolumeScore * RISK_WEIGHTS.dataVolume +
    offHoursScore * RISK_WEIGHTS.offHoursActivity +
    failedAttemptsScore * RISK_WEIGHTS.failedAttempts +
    uniqueIPScore * RISK_WEIGHTS.uniqueIPs
  );

  // Ensure score is between 0 and 100
  return Math.min(100, Math.max(0, Math.round(riskScore)));
};

/**
 * Gets severity classification based on risk score
 * @param {number} riskScore - Risk score between 0 and 100
 * @returns {Object} Severity level object with min, max, label, and color
 */
export const getSeverityLevel = (riskScore) => {
  if (riskScore >= SEVERITY_LEVELS.CRITICAL.min) return SEVERITY_LEVELS.CRITICAL;
  if (riskScore >= SEVERITY_LEVELS.HIGH.min) return SEVERITY_LEVELS.HIGH;
  if (riskScore >= SEVERITY_LEVELS.MEDIUM.min) return SEVERITY_LEVELS.MEDIUM;
  return SEVERITY_LEVELS.LOW;
};

/**
 * Calculates trend direction by comparing current and previous values
 * @param {number} current - Current value
 * @param {number} previous - Previous period value
 * @returns {Object} Trend object with direction ('up', 'down', 'neutral') and percentage change
 */
export const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) {
    return { direction: 'neutral', percentage: 0 };
  }

  const percentageChange = ((current - previous) / previous) * 100;

  if (Math.abs(percentageChange) < 5) {
    return { direction: 'neutral', percentage: 0 };
  }

  return {
    direction: percentageChange > 0 ? 'up' : 'down',
    percentage: Math.abs(Math.round(percentageChange))
  };
};

/**
 * Formats a risk score for display with severity color
 * @param {number} riskScore - Risk score between 0 and 100
 * @returns {Object} Display object with score, severity, and color
 */
export const formatRiskScore = (riskScore) => {
  const severity = getSeverityLevel(riskScore);
  return {
    score: riskScore,
    severity: severity.label,
    color: severity.color,
    displayText: `${riskScore} (${severity.label})`
  };
};

/**
 * Calculates overall security posture score from multiple risk category scores
 * @param {Array<number>} riskScores - Array of risk scores to average
 * @returns {number} Overall security posture score (0-100)
 */
export const calculateOverallPosture = (riskScores) => {
  if (!riskScores || riskScores.length === 0) return 0;
  
  const sum = riskScores.reduce((total, score) => total + score, 0);
  return Math.round(sum / riskScores.length);
};

/**
 * Gets posture label based on overall score
 * @param {number} score - Overall posture score (0-100)
 * @returns {string} Posture label
 */
export const getPostureLabel = (score) => {
  if (score >= 70) return 'CRITICAL';
  if (score >= 40) return 'ELEVATED';
  if (score >= 20) return 'MODERATE';
  return 'HEALTHY';
};

/**
 * Gets posture color based on overall score
 * @param {number} score - Overall posture score (0-100)
 * @returns {string} Color hex code
 */
export const getPostureColor = (score) => {
  if (score >= 70) return '#dc3545';
  if (score >= 40) return '#fd7e14';
  if (score >= 20) return '#ffc107';
  return '#28a745';
};

/**
 * Generates a link to Microsoft 365 Defender portal for a specific incident
 * @param {string} incidentType - Type of incident (e.g., 'DLP', 'InsiderRisk', 'Intune', 'EntraID')
 * @param {string} incidentId - Incident identifier
 * @returns {string} URL to the Microsoft 365 Defender portal
 */
export const getDefenderPortalLink = (incidentType, incidentId = '') => {
  const baseUrl = 'https://security.microsoft.com';
  
  switch (incidentType) {
    case 'DLP':
      return `${baseUrl}/datalossprevention/incidents${incidentId ? `/${incidentId}` : ''}`;
    case 'InsiderRisk':
      return `${baseUrl}/insiderrisk/cases${incidentId ? `/${incidentId}` : ''}`;
    case 'Intune':
      return `${baseUrl}/devices${incidentId ? `/${incidentId}` : ''}`;
    case 'EntraID':
      return `${baseUrl}/identityprotection${incidentId ? `/${incidentId}` : ''}`;
    case 'AdvancedHunting':
      return `${baseUrl}/v2/advanced-hunting`;
    default:
      return `${baseUrl}/incidents`;
  }
};
